import React from 'react'
import { observer } from 'mobx-react'
import { QRCodeSVG } from 'qrcode.react'
import { createStyles, makeStyles, Paper, Theme, Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useParamConversation, useParamTopic } from '../../../Providers'

/**
 * Who is allowed to see the story QR code on the conversation page.
 *
 * The conversation page is public -- SlugPage.tsx routes it through
 * `PageWithProviders`, not `LoggedInPageWithProviders` -- so a code rendered
 * for everyone is, in effect, a permanently published "submit a story to this
 * dinner" link that anyone who finds the page can scan.
 *
 * Whether that is wanted is an open question with the client, so the default
 * here is the reversible one: only the people running the event see it. That
 * still serves the actual use case (a host puts the page on a screen and the
 * room scans it) without widening who can post.
 *
 * Flipping this constant to `true` -- or passing `visibleToEveryone` at the
 * call site -- is the whole change if the answer comes back the other way.
 */
export const STORY_QR_VISIBLE_TO_EVERYONE = false

interface Props {
  /** Defaults to {@link STORY_QR_VISIBLE_TO_EVERYONE}. */
  visibleToEveryone?: boolean
  /** Rendered size in pixels. Big enough to scan from across a room. */
  size?: number
}

/**
 * A QR code encoding this conversation's story page, for a host to display on
 * a screen during the dinner.
 *
 * Renders nothing unless the topic has stories turned on, mirroring the same
 * `storiesEnabled` check that guards the Stories section itself.
 */
export const StoryQrCode = observer(({ visibleToEveryone = STORY_QR_VISIBLE_TO_EVERYONE, size = 176 }: Props) => {
  const { conversation } = useParamConversation()
  const { topic } = useParamTopic()
  const { t } = useTranslation()
  const classes = Styles()

  if (!conversation || !topic?.storiesEnabled) return <></>
  // `canEdit` is host, super admin, or the community admin for this topic --
  // the people who could be running the room.
  if (!visibleToEveryone && !conversation.canEdit) return <></>

  const storyLink = conversation.storyLink

  return (
    <section className={classes.root}>
      <Paper elevation={0} className={classes.card}>
        <Typography variant='subtitle1' align='center'>
          {t('story.qrHeader')}
        </Typography>
        <div className={classes.code}>
          <QRCodeSVG value={storyLink} size={size} level='M' marginSize={2} title={t('story.qrImageTitle')} />
        </div>
      </Paper>
    </section>
  )
})

const Styles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: '2em auto',
      textAlign: 'center'
    },
    card: {
      display: 'inline-block',
      maxWidth: '100%',
      padding: theme.spacing(3),
      border: '1px solid #E0E0E0',
      borderRadius: '8px'
    },
    code: {
      margin: '1em auto 0 auto',
      lineHeight: 0
    }
  })
)

export default StoryQrCode
